// Cola offline de movimientos, persistida en IndexedDB.
//
// Regla de oro: NADA se pierde. Un movimiento se guarda en el telefono
// ANTES de intentar enviarlo. Si hay conexion, se envia y se quita; si no,
// se queda y se reenvia al reconectar. La clave de idempotencia asegura
// que un reenvio no duplique existencias en el servidor.
import { api, ErrorApi } from '../api';
import { uuidv7 } from './uuidv7';

const DB = 'tego-cola';
const STORE = 'movimientos';

function abrir() {
  return new Promise((res, rej) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => {
      if (!r.result.objectStoreNames.contains(STORE)) {
        r.result.createObjectStore(STORE, { keyPath: 'clave_idempotencia' });
      }
    };
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}

function conStore(modo, fn) {
  return abrir().then(
    (d) =>
      new Promise((res, rej) => {
        const t = d.transaction(STORE, modo);
        const rq = fn(t.objectStore(STORE));
        t.oncomplete = () => res(rq && rq.result);
        t.onerror = () => rej(t.error);
        t.onabort = () => rej(t.error);
      }),
  );
}

const guardar = (m) => conStore('readwrite', (s) => s.put(m));
const quitar = (clave) => conStore('readwrite', (s) => s.delete(clave));
export const pendientes = () => conStore('readonly', (s) => s.getAll());
export async function contarPendientes() {
  return (await pendientes()).length;
}

// Lo que se manda al backend (sin campos internos de la cola).
function carga(m) {
  const { _ts, _error, ...resto } = m;
  return resto;
}

// Un error de negocio (400/409/422) no se resuelve reintentando: el
// movimiento no procede (p. ej. stock insuficiente). Los de red/servidor
// (o 401/429) si se reintentan luego.
function esDefinitivo(e) {
  return e instanceof ErrorApi && [400, 404, 409, 422].includes(e.status);
}

/**
 * Registra un movimiento. Devuelve:
 *   {estado:'aplicado', movimiento}    — se envio y el stock ya cambio
 *   {estado:'pendiente', solicitud}    — supero el umbral: espera autorizacion
 *                                        y el stock NO ha cambiado
 *   {estado:'encolado'}                — sin conexion; guardado en el telefono
 * Lanza ErrorApi si el movimiento es rechazado por regla de negocio.
 */
export async function registrarMovimiento(mov) {
  const m = { ...mov, clave_idempotencia: mov.clave_idempotencia || uuidv7(), _ts: Date.now() };
  await guardar(m); // durabilidad primero

  if (!navigator.onLine) return { estado: 'encolado' };
  try {
    const respuesta = await api.post('/inventario/movimientos', carga(m));
    await quitar(m.clave_idempotencia);
    // El backend responde 202 con este estado cuando la doble autorizacion
    // atrapa el movimiento. Se distingue aqui para que ninguna pantalla
    // pueda decirle al operador "listo" cuando en realidad no se aplico.
    if (respuesta?.estado === 'PENDIENTE_APROBACION') {
      return { estado: 'pendiente', solicitud: respuesta };
    }
    return { estado: 'aplicado', movimiento: respuesta };
  } catch (e) {
    if (esDefinitivo(e)) {
      await quitar(m.clave_idempotencia);
      throw e;
    }
    return { estado: 'encolado' };
  }
}

/** Reenvia todo lo pendiente. Devuelve {enviados, rechazados, restantes}. */
export async function sincronizar() {
  const cola = await pendientes();
  let enviados = 0;
  const rechazados = [];
  for (const m of cola) {
    try {
      await api.post('/inventario/movimientos', carga(m));
      await quitar(m.clave_idempotencia);
      enviados += 1;
    } catch (e) {
      if (esDefinitivo(e)) {
        await quitar(m.clave_idempotencia);
        rechazados.push({ ...m, _error: e.message });
      }
      // Errores de red/servidor: se deja en cola para el proximo intento.
    }
  }
  return { enviados, rechazados, restantes: await contarPendientes() };
}
